import { MEETING_WINDOW_PARENT_ORIGIN_PARAM, MEETING_WINDOW_QUERY_FLAG } from './protocol';

/**
 * `sessionStorage` keys mirroring the popup query params.
 *
 * The params cannot be the durable source of truth: `useMeetingLink` clears the
 * whole query string via `history.replaceState`, so anything re-reading
 * `location.search` later — including a full page reload, which starts from the
 * already-cleaned URL — would wrongly conclude this is not a popup and send the
 * window down the standalone PKCE path mid-meeting. `sessionStorage` is scoped
 * to this tab and survives reloads, which is exactly the lifetime of a meeting
 * window, and it keeps the params out of the visible URL.
 */
const POPUP_FLAG_STORAGE_KEY = 'ngmeet-meeting-popup';
const POPUP_PARENT_ORIGIN_STORAGE_KEY = 'ngmeet-meeting-popup-parent-origin';

const readSessionValue = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionValue = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Private-mode or storage-disabled: fall back to the in-memory cache only.
  }
};

/**
 * Read the popup query params, latching them into `sessionStorage` the first
 * time we see them so later reads (and reloads) still resolve correctly.
 */
const readPopupParam = (param: string, storageKey: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(param);
    if (fromUrl !== null) {
      writeSessionValue(storageKey, fromUrl);
      return fromUrl;
    }
  } catch {
    // fall through to the latched value
  }
  return readSessionValue(storageKey);
};

const computeIsMeetingPopup = (): boolean => {
  // `window.opener` survives same-origin reloads, so it remains a valid guard
  // against an unrelated tab inheriting a latched flag.
  if (typeof window === 'undefined' || !window.opener || window.opener === window) {
    return false;
  }
  return readPopupParam(MEETING_WINDOW_QUERY_FLAG, POPUP_FLAG_STORAGE_KEY) === '1';
};

// Cached once per page so repeat callers don't re-parse; the `sessionStorage`
// latch above is what makes the answer stable across the URL being cleaned and
// across reloads.
let cachedIsMeetingPopup: boolean | null = null;

/**
 * True when the current window is a meeting popup launched by a hosted NG Meet
 * instance — i.e. it has an opener AND carried the `?popup=1` flag we set when
 * opening it. Used at startup to pick the popup auth handoff path instead of
 * the standalone PKCE flow, and later to close the window on meeting end.
 */
export const isMeetingPopup = (): boolean => {
  if (cachedIsMeetingPopup === null) {
    cachedIsMeetingPopup = computeIsMeetingPopup();
  }
  return cachedIsMeetingPopup;
};

/**
 * Read and latch the popup identity while the query params are still on the URL.
 *
 * Both values latch lazily on first read, which leaves them dependent on
 * something happening to ask before `useMeetingLink` clears the query string in
 * an effect. That ordering holds today but is invisible and easy to break in a
 * refactor, so callers should invoke this explicitly at startup, before React
 * mounts, to make the dependency a stated one.
 */
export const latchMeetingPopupIdentity = (): void => {
  isMeetingPopup();
  getMeetingPopupParentOrigin();
};

// Small grace period before closing so in-flight teardown (meeting span end,
// telemetry export) has a chance to flush.
const MEETING_POPUP_CLOSE_DELAY_MS = 400;

/**
 * Close the meeting popup window if we are running inside one. Returns `true`
 * when it acted (popup mode) so callers can skip their standalone
 * navigate-home fallback.
 */
export const closeMeetingPopup = (): boolean => {
  if (!isMeetingPopup()) {
    return false;
  }
  window.setTimeout(() => window.close(), MEETING_POPUP_CLOSE_DELAY_MS);
  return true;
};

/**
 * The opener's origin, originally stamped on the popup URL as `parentOrigin`.
 * Used as the `targetOrigin` for postMessage and to validate inbound messages.
 * Latched into `sessionStorage` on first read so it outlives both the URL being
 * cleaned and a reload.
 */
export const getMeetingPopupParentOrigin = (): string | null =>
  readPopupParam(MEETING_WINDOW_PARENT_ORIGIN_PARAM, POPUP_PARENT_ORIGIN_STORAGE_KEY);
